import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, RefreshCw, AlertCircle, Eye, Users, MessageCircle, Search, Save, Bot, Mail, Activity } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const growthPct = (cur, prev) => {
  if (!prev) return null;
  return (((cur - prev) / prev) * 100).toFixed(1);
};

const GrowthBadge = ({ cur, prev }) => {
  const g = growthPct(cur, prev);
  if (g === null) return <span className="text-[11px] text-slate-500">mês anterior: {prev}</span>;
  const pos = Number(g) >= 0;
  return (
    <span className={`text-[11px] font-medium ${pos ? 'text-emerald-400' : 'text-red-400'}`}>
      {pos ? '▲' : '▼'} {Math.abs(g)}% vs mês anterior ({prev})
    </span>
  );
};

const KpiCard = ({ icon: Icon, label, value, color, children, testid }) => (
  <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-4" data-testid={testid}>
    <div className="flex items-center gap-2 mb-1">
      <Icon className={`w-4 h-4 ${color}`} />
      <span className="text-xs text-slate-400">{label}</span>
    </div>
    <p className={`text-2xl font-extrabold ${color}`}>{value}</p>
    {children}
  </div>
);

const CHANNELS = [
  { key: 'whatsapp', label: 'WhatsApp', icon: MessageCircle, color: 'bg-emerald-500', text: 'text-emerald-400' },
  { key: 'chatbot', label: 'Chatbot IA', icon: Bot, color: 'bg-cyan-500', text: 'text-cyan-400' },
  { key: 'form', label: 'Formulário', icon: Mail, color: 'bg-blue-500', text: 'text-blue-400' },
];

export default function AdminMetrics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [years, setYears] = useState('');
  const [satisfaction, setSatisfaction] = useState(100);
  const [saving, setSaving] = useState(false);

  const authHeaders = () => ({ 'Authorization': `Bearer ${sessionStorage.getItem('admin_pwd') || ''}` });

  const fetchMetrics = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/site-metrics`, { headers: authHeaders() });
      if (!res.ok) throw new Error('Erro ao carregar métricas');
      const d = await res.json();
      setData(d);
      setYears(d.site_stats?.years_experience ?? '');
      setSatisfaction(d.site_stats?.satisfaction ?? 100);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchMetrics(); }, [fetchMetrics]);

  const saveStats = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/site-stats`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          years_experience: years === '' ? null : parseInt(years, 10),
          satisfaction: parseInt(satisfaction, 10),
        }),
      });
      if (!res.ok) throw new Error('Erro ao salvar');
      toast.success('Números do site atualizados!');
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex-1 flex justify-center items-center py-16"><Loader2 className="w-8 h-8 text-cyan-400 animate-spin" /></div>;
  if (error) return <div className="flex-1 flex items-center gap-2 text-red-400 text-sm py-8 justify-center"><AlertCircle className="w-4 h-4" />{error}</div>;
  if (!data) return null;

  const conv = data.current.conversions || {};
  const convPrev = data.previous.conversions || {};
  const maxChannel = Math.max(...CHANNELS.map(c => conv[c.key] || 0), 1);

  return (
    <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-6" data-testid="admin-metrics">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Activity className="w-5 h-5 text-cyan-400" />
          <h3 className="text-lg font-bold text-white">Métricas do Site — {data.month}</h3>
        </div>
        <button onClick={fetchMetrics} className="text-slate-400 hover:text-white p-1.5" title="Atualizar" data-testid="metrics-refresh-btn">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard icon={Users} label="Visitantes únicos no mês" value={data.current.visitors} color="text-cyan-400" testid="kpi-visitors">
          <GrowthBadge cur={data.current.visitors} prev={data.previous.visitors} />
        </KpiCard>
        <KpiCard icon={Eye} label="Páginas vistas" value={data.current.pageviews} color="text-blue-400" testid="kpi-pageviews">
          <GrowthBadge cur={data.current.pageviews} prev={data.previous.pageviews} />
        </KpiCard>
        <KpiCard icon={MessageCircle} label="Atendimentos iniciados" value={conv.total || 0} color="text-emerald-400" testid="kpi-conversions">
          <GrowthBadge cur={conv.total || 0} prev={convPrev.total || 0} />
        </KpiCard>
        <KpiCard icon={Search} label="Buscas internas" value={data.searches} color="text-amber-400" testid="kpi-searches" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Conversões por canal */}
        <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-5">
          <h4 className="text-sm font-bold text-white mb-4">Atendimentos por canal (mês atual)</h4>
          <div className="space-y-4">
            {CHANNELS.map(c => (
              <div key={c.key} data-testid={`channel-${c.key}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className={`flex items-center gap-1.5 text-xs font-medium ${c.text}`}>
                    <c.icon className="w-3.5 h-3.5" /> {c.label}
                  </span>
                  <span className="text-white text-sm font-bold tabular-nums">{conv[c.key] || 0}</span>
                </div>
                <div className="h-2 bg-slate-700/60 rounded-full overflow-hidden">
                  <div className={`h-full ${c.color} rounded-full transition-all`} style={{ width: `${((conv[c.key] || 0) / maxChannel) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-slate-500 mt-4">Cada visitante conta 1x por canal por sessão. A soma alimenta o badge público da home.</p>
        </div>

        {/* Números manuais */}
        <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-5">
          <h4 className="text-sm font-bold text-white mb-4">Números fixos do site (badges da home)</h4>
          <div className="space-y-4">
            <div>
              <label className="text-xs text-slate-400 block mb-1">Anos de experiência <span className="text-slate-600">(vazio = automático desde 2008)</span></label>
              <input type="number" min="0" max="100" value={years} onChange={e => setYears(e.target.value)}
                placeholder="Automático"
                className="w-full bg-slate-900 border border-slate-700 text-white text-sm rounded-lg px-3 py-2 outline-none focus:border-cyan-500"
                data-testid="input-years-experience" />
            </div>
            <div>
              <label className="text-xs text-slate-400 block mb-1">Satisfação garantida (%)</label>
              <input type="number" min="0" max="100" value={satisfaction} onChange={e => setSatisfaction(e.target.value)}
                className="w-full bg-slate-900 border border-slate-700 text-white text-sm rounded-lg px-3 py-2 outline-none focus:border-cyan-500"
                data-testid="input-satisfaction" />
            </div>
            <button onClick={saveStats} disabled={saving}
              className="flex items-center gap-2 bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
              data-testid="save-site-stats-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salvar
            </button>
          </div>
        </div>

        {/* Top páginas */}
        <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-5">
          <h4 className="text-sm font-bold text-white mb-3">Páginas mais acessadas</h4>
          {data.top_pages.length === 0 ? (
            <p className="text-slate-500 text-xs py-4 text-center">Sem visitas registradas ainda este mês.</p>
          ) : (
            <table className="w-full text-sm" data-testid="top-pages-table">
              <tbody>
                {data.top_pages.map(p => (
                  <tr key={p.page} className="border-b border-slate-700/40 last:border-0">
                    <td className="py-1.5 text-slate-300 truncate max-w-[220px]">{p.page}</td>
                    <td className="py-1.5 text-right text-cyan-400 font-bold tabular-nums">{p.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Top termos buscados */}
        <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-5">
          <h4 className="text-sm font-bold text-white mb-3">O que os visitantes procuram (busca interna)</h4>
          {data.top_terms.length === 0 ? (
            <p className="text-slate-500 text-xs py-4 text-center">Nenhuma busca registrada ainda este mês.</p>
          ) : (
            <div className="flex flex-wrap gap-2" data-testid="top-terms-list">
              {data.top_terms.map(t => (
                <span key={t.term} className="inline-flex items-center gap-1.5 bg-slate-900 border border-slate-700 rounded-full px-3 py-1 text-xs text-slate-300">
                  {t.term} <span className="text-amber-400 font-bold">{t.count}</span>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
